import React, { useState, useEffect, lazy, Suspense, useCallback, useMemo } from "react";
import { Flex, Text, IconButton, HoverCard, Tooltip, Box, Button, Separator, SegmentedControl, Badge, Popover, TextField, Card, Spinner } from "@radix-ui/themes";
import { ArrowLeftIcon, PlusIcon, Cross2Icon } from "@radix-ui/react-icons";
import { ComputerDesktopIcon, DevicePhoneMobileIcon } from "@heroicons/react/16/solid";
import { useProfileContext, useLocationContext } from "context";
import { useParams, useLocation as useRouterLocation, useNavigate } from "react-router-dom";
import {
  useLiveAccessPoints,
  useGetBrandGuestPortalSettings,
  useGetLocationGuestPortalSettings,
  useGetOrganisationDetails,
  useGetGuestPortalSettingsBanner,
  useGetGuestPortalSettingsLinks,
  useGetPlansGuestPortalSettings,
  useCreatePlanGuestPortalSettings,
  useGetGuestPortalSettingsById,
  useGetGuestPortalPeriods,
  useCreateGuestPortalPeriod,
  useUpdateGuestPortalPeriod,
  useDeleteGuestPortalPeriod,
  useGetPeriodGuestPortalSettings,
  useUpdatePeriodGuestPortalSettings,
} from "hooks";
import { IPhoneMockup } from "react-device-mockup";
import { debounce } from "lodash";
import Calendar from "./CalendarPlanner";
import { DatePickerWithRange } from "components/datepicker/DatePickerWithRange";

const CaptivePortalMockup = lazy(() => import("./CaptivePortalMockup"));

// Utility function to check if two date ranges overlap
const checkDateRangeOverlap = (start1, end1, start2, end2) => {
  const parseDate = (dateString) => new Date(dateString);

  const startDate1 = parseDate(start1);
  const endDate1 = parseDate(end1);
  const startDate2 = parseDate(start2);
  const endDate2 = parseDate(end2);

  // Allow for end date of one plan to equal start date of another
  if (startDate1.getTime() === endDate2.getTime() || startDate2.getTime() === endDate1.getTime()) {
    return false; // These are consecutive plans, not overlapping
  }

  // Check for actual overlap
  return startDate1 < endDate2 && startDate2 < endDate1;
};

// Function to find overlapping plans
const findOverlappingPlans = (plans, newStartDate, newEndDate, excludePlanId = null) => {
  return plans.filter((plan) => {
    // Skip the plan being edited (for update scenarios)
    if (excludePlanId && plan.id === excludePlanId) {
      return false;
    }

    // Skip main plans as they typically represent the default state
    if (plan.is_main) {
      return false;
    }

    return checkDateRangeOverlap(plan.start_date, plan.end_date, newStartDate, newEndDate);
  });
};

const CreatePlanPopover = ({ isOpen, onOpenChange, planTitle, setPlanTitle, planStartDate, 
  setPlanStartDate, planEndDate, setPlanEndDate, onCreatePlan, isCreating,
  children, validationError, setValidationError }) => {
  const resetForm = useCallback(() => {
    setPlanTitle("");
    setPlanStartDate("");
    setPlanEndDate("");
    if (setValidationError) {
      setValidationError("");
    }
  }, [setPlanTitle, setPlanStartDate, setPlanEndDate, setValidationError]);

  // Handle date range changes from the DatePickerWithRange component
  const handleDateRangeChange = useCallback(
    (dateRange) => {
      if (dateRange?.from && dateRange?.to) {
        // Use a more reliable method to format dates that avoids timezone issues
        const formatDateForBackend = (date) => {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, "0");
          const day = String(date.getDate()).padStart(2, "0");
          return `${year}-${month}-${day}`;
        };

        const startDate = formatDateForBackend(dateRange.from);
        const endDate = formatDateForBackend(dateRange.to);

        setPlanStartDate(startDate);
        setPlanEndDate(endDate);

        // Clear validation error when dates change
        if (setValidationError) {
          setValidationError("");
        }
      } else {
        setPlanStartDate("");
        setPlanEndDate("");
      }
    },
    [setPlanStartDate, setPlanEndDate, setValidationError]
  );

  // Convert current dates to the format expected by DatePickerWithRange
  const currentDateRange = useMemo(() => {
    return planStartDate && planEndDate
      ? {
          from: new Date(planStartDate),
          to: new Date(planEndDate),
        }
      : { from: null, to: null };
  }, [planStartDate, planEndDate]);

  // Handle close with reset
  const handleClose = useCallback(() => {
    resetForm();
    onOpenChange(false);
  }, [resetForm, onOpenChange]);

  const handleTitleChange = useCallback(
    (e) => {
      setPlanTitle(e.target.value);
      // Clear validation error when title changes
      if (setValidationError) {
        setValidationError("");
      }
    },
    [setPlanTitle, setValidationError]
  );

  return (
    <Popover.Root open={isOpen} onOpenChange={onOpenChange}>
      <Popover.Trigger asChild>{children}</Popover.Trigger>
      <Popover.Content side="bottom" align="end" width="296px">
        <Flex gap="4" direction="column">
          <Flex style={{ alignItems: "flex-start", justifyContent: "space-between" }}>
            <Text size="3" weight="medium">
              Create portal
            </Text>
            <IconButton variant="ghost" color="gray" size="2" style={{ alignSelf: "flex-end" }} onClick={handleClose}>
              <Cross2Icon width={16} height={16} />
            </IconButton>
          </Flex>
          <Flex direction="column" gap="2">
            <Flex direction="column" gap="1" style={{ alignItems: "flex-start" }}>
              <Text size="2" style={{ alignSelf: "stretch" }}>
                Portal name
              </Text>
              <TextField.Root size="2" weight="medium" style={{ width: "100%" }} value={planTitle} onChange={handleTitleChange} />
            </Flex>
            <Flex direction="column" gap="1" style={{ alignItems: "flex-start" }}>
              <Text size="2" style={{ alignSelf: "stretch" }}>
                Date range
              </Text>
              <DatePickerWithRange
                value={currentDateRange}
                onDateChange={handleDateRangeChange}
                disableBeforeDate={new Date()} // Disable past dates
                numberOfMonths={1}
                allowFutureDates={true}
                className="w-full"
              />
            </Flex>
            {validationError && (
              <Flex direction="column" gap="1">
                <Text size="2" color="red" style={{ fontWeight: 500 }}>
                  {validationError}
                </Text>
              </Flex>
            )}
          </Flex>
          <Flex style={{ alignItems: "center", alignSelf: "stretch" }}>
            <Button
              variant="solid"
              style={{ flex: "1 0 0" }}
              disabled={isCreating || !planTitle || !planStartDate || !planEndDate || !!validationError}
              loading={isCreating}
              onClick={onCreatePlan}>
              Create
            </Button>
          </Flex>
        </Flex>
      </Popover.Content>
    </Popover.Root>
  );
};

const CaptivePortalPlanner = () => {
  const { userProfile } = useProfileContext();
  const params = useParams();
  const routerLocation = useRouterLocation();
  const navigate = useNavigate();
  const [view, setView] = useState("mobile");

  // Utility function for adjusting end dates for backend storage
  const adjustEndDateForBackend = (endDateString) => {
    const d = new Date(endDateString);
    d.setDate(d.getDate() + 1);
    // Use manual formatting to avoid timezone issues
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // screen sizing for IPhoneMockup
  const [screenWidthMobile, setScreenWidthMobile] = useState(250);
  const [scaleFactorMobile, setScaleFactorMobile] = useState(1);

  // Set initial screen width and scale factor based on window size
  useEffect(() => {
    const handleResize = debounce(() => {
      const windowH = window.innerHeight;
      const windowW = window.innerWidth;
      const aspectRatio = 19.5 / 9;
      const maxWidth = 390;
      const offset = 300;
      const availH = windowH - offset;
      const newW = Math.min(maxWidth, availH / aspectRatio, windowW);
      setScreenWidthMobile(newW);
      setScaleFactorMobile(newW / maxWidth);
    }, 100);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      handleResize.cancel?.();
    };
  }, []);

  // Determine if we're viewing a brand planner or location planner and define paramId accordingly
  const isBrandRoute = routerLocation.pathname.includes("/portal-brand/");
  const isLocationRoute = routerLocation.pathname.includes("/portal-location/");
  const paramId = isBrandRoute ? parseInt(params.brandId) : parseInt(params.locationId);

  // Get the brand info from router state
  const { isBrand, brandId, brandName, brandLocationCount, locationName, locationAddress } = routerLocation.state || {};

  // Get location context for additional location data if needed
  const { locationsData } = useLocationContext();
  const { liveAccessPointsStatus } = useLiveAccessPoints(locationsData);

  const [activeTab, setActiveTab] = useState("portal");

  // Color management for the button and background overlay
  const [buttonColor, setButtonColor] = useState("#3807D9");
  const [backgroundOverlayColor, setBackgroundOverlayColor] = useState("#000000");
  const [backgroundOverlayOpacity, setBackgroundOverlayOpacity] = useState(50);
  const [backgroundPreview, setBackgroundPreview] = useState(null);
  function getButtonTextColorBasedOnBackground(buttonBackgroundColor) {
    if (!buttonBackgroundColor) return "#FFFFFF";
    buttonBackgroundColor = buttonBackgroundColor.replace("#", "");
    // Converting the hex color to RGB
    const r = parseInt(buttonBackgroundColor.substring(0, 2), 16) / 255;
    const g = parseInt(buttonBackgroundColor.substring(2, 4), 16) / 255;
    const b = parseInt(buttonBackgroundColor.substring(4, 6), 16) / 255;

    // Calculating luminance using the formula
    const luminance =
      0.2126 * (r <= 0.03928 ? r / 12.92 : Math.pow((r + 0.055) / 1.055, 2.4)) +
      0.7152 * (g <= 0.03928 ? g / 12.92 : Math.pow((g + 0.055) / 1.055, 2.4)) +
      0.0722 * (b <= 0.03928 ? b / 12.92 : Math.pow((b + 0.055) / 1.055, 2.4));

    // If luminance is higher than 0.5, using black text; otherwise, white text
    return luminance > 0.5 ? "#000000" : "#FFFFFF";
  }

  const [buttonTextColor, setButtonTextColor] = useState(getButtonTextColorBasedOnBackground(buttonColor));
  useEffect(() => {
    setButtonTextColor(getButtonTextColorBasedOnBackground(buttonColor));
  }, [buttonColor]);

  function colorConversion(inputColour, opacity) {
    const alpha = Math.round(opacity * 2.55)
      .toString(16)
      .padStart(2, "0")
      .toUpperCase();
    return `${inputColour}${alpha}`;
  }
  useEffect(() => {
    colorConversion(backgroundOverlayColor, backgroundOverlayOpacity);
  }, [backgroundOverlayColor, backgroundOverlayOpacity]);

  const { data: orgDetails } = useGetOrganisationDetails(userProfile.organisation_id);

  const [effectiveLocationId, setEffectiveLocationId] = useState(null);
  const [useBrandSettings, setUseBrandSettings] = useState(false);
  const { data: plansData } = useGetPlansGuestPortalSettings(brandId, effectiveLocationId);

  // Find a location ID for the brand route
  useEffect(() => {
    if (isBrandRoute && locationsData && plansData?.data) {
      // For brand routes, prefer the location that owns the main plan
      const mainPlan = plansData.data.find((plan) => plan.is_main);

      if (mainPlan?.location) {
        // Use the main plan's location as the effective location
        setEffectiveLocationId(mainPlan.location);
        setUseBrandSettings(false);
      } else {
        // Fallback: Find the first location that belongs to this brand
        const locationForBrand = locationsData.find((location) => location.brand_id === paramId);
        if (locationForBrand) {
          setEffectiveLocationId(locationForBrand.id);
          setUseBrandSettings(false);
        } else {
          // If no location found for this brand then we use the brandId directly
          setEffectiveLocationId(paramId);
          setUseBrandSettings(true);
        }
      }
    } else if (isLocationRoute) {
      // For location route, use the locationId directly
      setEffectiveLocationId(paramId);
      setUseBrandSettings(false);
    }
  }, [isBrandRoute, isLocationRoute, paramId, locationsData, plansData?.data, navigate]);

  const { data: brandGuestPortalSettings } = useGetBrandGuestPortalSettings(
    useBrandSettings ? effectiveLocationId : null // Only fetch if useBrandSettings is true
  );
  const { data: locationGuestPortalSettings } = useGetLocationGuestPortalSettings(
    !useBrandSettings ? effectiveLocationId : null // Only fetch if useBrandSettings is false
  );
  const guestPortalSettings = useBrandSettings ? brandGuestPortalSettings : locationGuestPortalSettings;
  const { data: bannerData } = useGetGuestPortalSettingsBanner(guestPortalSettings?.data.banner_flag ? guestPortalSettings?.data.id : null);
  const { data: linksData } = useGetGuestPortalSettingsLinks(guestPortalSettings?.data.links_flag ? guestPortalSettings?.data.id : null);
  const [links, setLinks] = useState([]);

  useEffect(() => {
    if (guestPortalSettings?.data) {
      setBackgroundPreview(guestPortalSettings.data.background_image);
      setButtonColor(guestPortalSettings.data.button_colour);
      setBackgroundOverlayColor(guestPortalSettings.data.background_overlay_colour);
      setBackgroundOverlayOpacity(guestPortalSettings.data.background_overlay_opacity * 100);
    }
    if (linksData?.data) {
      setLinks(linksData.data);
    }
  }, [linksData, guestPortalSettings]);

  // Plans
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  useEffect(() => {
    // Only set initially if selectedPlan is null and all necessary data is available
    if (selectedPlan === null && plansData?.data && guestPortalSettings?.data?.active_plan) {
      const activePlanFromServer = guestPortalSettings.data.active_plan;
      // Find the full plan object from plansData.data using the id from active_plan
      // This ensures that the selectedPlan object has the complete structure, including 'periods' if applicable.
      const planToSelect = plansData.data.find((p) => p.id === activePlanFromServer.id);
      if (planToSelect) {
        setSelectedPlan(planToSelect);
      }
    }
  }, [plansData, guestPortalSettings, selectedPlan]); // Dependencies: re-run if these change, but condition selectedPlan === null prevents overriding user's subsequent selections.

  // Adding hook to fetch selected plan's guest portal settings
  const { data: selectedPlanGuestPortalSettings } = useGetGuestPortalSettingsById(selectedPlan?.periods?.[0]?.guest_portal_setting || null);

  // Add state for selected plan's settings
  const [selectedPlanLinks, setSelectedPlanLinks] = useState([]);
  const [selectedPlanButtonColor, setSelectedPlanButtonColor] = useState("#3807D9");
  const [selectedPlanBackgroundOverlayColor, setSelectedPlanBackgroundOverlayColor] = useState("#000000");
  const [selectedPlanBackgroundOverlayOpacity, setSelectedPlanBackgroundOverlayOpacity] = useState(50);
  const [selectedPlanBackgroundPreview, setSelectedPlanBackgroundPreview] = useState(null);
  // Added for period handling
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const { data: periodsData, isLoading: isLoadingPeriods } = useGetGuestPortalPeriods(selectedPlan?.id || null);

  // React to periods loading
  useEffect(() => {
    // Effect kept for potential future logic when periods load
  }, [periodsData]);

  const { data: periodGuestPortalSettings, isLoading: isLoadingPeriodSettings } = useGetPeriodGuestPortalSettings(selectedPeriod?.guest_portal_setting || null);

  // Get settings based on selected period or plan
  const displayGuestPortalSettings = selectedPeriod && periodGuestPortalSettings ? periodGuestPortalSettings : selectedPlanGuestPortalSettings;

  // Monitor settings changes
  useEffect(() => {
    // This effect monitors settings changes for the selected period or plan
    // No debug logging needed in production
  }, [periodGuestPortalSettings, selectedPlanGuestPortalSettings, displayGuestPortalSettings]);

  useEffect(() => {
    // When the selected plan or period settings change, update the UI
    const settingsToUse = displayGuestPortalSettings?.data?.settings;

    if (settingsToUse) {
      setSelectedPlanBackgroundPreview(settingsToUse.background_image);
      setSelectedPlanButtonColor(settingsToUse.button_colour);
      setSelectedPlanBackgroundOverlayColor(settingsToUse.background_overlay_colour);
      setSelectedPlanBackgroundOverlayOpacity(parseFloat(settingsToUse.background_overlay_opacity) * 100);
    }

    if (displayGuestPortalSettings?.data?.links) {
      setSelectedPlanLinks(displayGuestPortalSettings.data.links);
    }
  }, [selectedPlanGuestPortalSettings, periodGuestPortalSettings, displayGuestPortalSettings]);

  // Get selected plan's button text color
  const selectedPlanButtonTextColor = getButtonTextColorBasedOnBackground(selectedPlanButtonColor);

  // 1. Initialize the createPlan mutation & form state
  const { mutate: createPlan, isLoading: creatingPlan } = useCreatePlanGuestPortalSettings();

  const [planTitle, setPlanTitle] = useState("");
  const [planStartDate, setPlanStartDate] = useState("");
  const [planEndDate, setPlanEndDate] = useState("");

  // Validation error states
  const [validationError, setValidationError] = useState("");
  const [hoverValidationError, setHoverValidationError] = useState("");

  const formatDate = (dateString, adjustment = 0) => {
    const [year, month, day] = dateString.split("-");
    const d = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    d.setDate(d.getDate() + adjustment);
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  };

  const handleSelectPlan = (plan) => {
    handlePlanClick(plan);
  };

  // Determine which settings to display in mockup using our helper function
  const bannerDataToUse =
    selectedPeriod && periodGuestPortalSettings?.data?.banners?.[0]
      ? { data: periodGuestPortalSettings.data.banners[0] }
      : selectedPlan && selectedPlanGuestPortalSettings?.data?.banners?.[0]
      ? { data: selectedPlanGuestPortalSettings.data.banners[0] }
      : bannerData;

  const displayBannerData = bannerDataToUse;
  const displayLinks = selectedPlan ? (selectedPeriod && periodGuestPortalSettings?.data?.links ? periodGuestPortalSettings.data.links : selectedPlanGuestPortalSettings?.data?.links || []) : links;

  // Use the saved state for visual settings when a plan is selected, otherwise use default
  const displayButtonColor = selectedPlan ? selectedPlanButtonColor : buttonColor;
  const displayButtonTextColor = selectedPlan ? selectedPlanButtonTextColor : buttonTextColor;
  const displayBackgroundPreview = selectedPlan ? selectedPlanBackgroundPreview : backgroundPreview;
  const displayBackgroundOverlayColor = selectedPlan ? selectedPlanBackgroundOverlayColor : backgroundOverlayColor;
  const displayBackgroundOverlayOpacity = selectedPlan ? selectedPlanBackgroundOverlayOpacity : backgroundOverlayOpacity;

  function formatDateMinusOne(dateString) {
    if (!dateString) return "";
    const d = new Date(dateString);
    d.setDate(d.getDate() - 1);
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  }

  // For calendar planner
  const handleDateSelect = (date) => {
    // This function can be used for future functionality if needed
    console.log("Selected date:", date);
  };

  const displayPlans = plansData?.data?.filter((plan) => !plan.is_main) || [];

  // Helper function to extract settings from various data structures
  const extractSetting = (settingKey) => {
    // First check period-specific settings if we have a selected period
    if (selectedPeriod && periodGuestPortalSettings?.data) {
      // Check in settings object
      if (periodGuestPortalSettings.data?.settings && periodGuestPortalSettings.data.settings[settingKey] !== undefined) {
        return periodGuestPortalSettings.data.settings[settingKey];
      }

      // Check in data root
      if (periodGuestPortalSettings.data[settingKey] !== undefined) {
        return periodGuestPortalSettings.data[settingKey];
      }

      // Check in overrides if exists
      if (selectedPeriod.overrides && selectedPeriod.overrides[settingKey] !== undefined) {
        return selectedPeriod.overrides[settingKey];
      }
    }

    // Next check plan settings
    if (selectedPlanGuestPortalSettings?.data) {
      // Check in settings object
      if (selectedPlanGuestPortalSettings.data?.settings && selectedPlanGuestPortalSettings.data.settings[settingKey] !== undefined) {
        return selectedPlanGuestPortalSettings.data.settings[settingKey];
      }

      // Check in data root
      if (selectedPlanGuestPortalSettings.data[settingKey] !== undefined) {
        return selectedPlanGuestPortalSettings.data[settingKey];
      }
    }

    // Finally fall back to default settings
    if (guestPortalSettings?.data) {
      if (guestPortalSettings.data[settingKey] !== undefined) {
        return guestPortalSettings.data[settingKey];
      }
    }

    // Return appropriate default values based on the setting key
    if (settingKey === "button_colour" || settingKey === "buttonColor") {
      return "#3807D9";
    }
    if (settingKey.includes("flag")) {
      return false;
    }

    return null;
  };

  const handleCreatePlan = () => {
    const adjustedEnd = adjustEndDateForBackend(planEndDate);

    // Check for overlapping plans
    const allPlans = plansData?.data || [];
    const overlappingPlans = findOverlappingPlans(allPlans, planStartDate, adjustedEnd);

    if (overlappingPlans.length > 0) {
      const overlappingPlanNames = overlappingPlans.map((plan) => plan.title).join(", ");
      setValidationError(`This date range overlaps with an existing planned portal: ${overlappingPlanNames}`);
      return;
    }

    createPlan(
      {
        formData: {
          organisation: userProfile.organisation_id,
          ...(isBrandRoute ? { brand: paramId } : { location: effectiveLocationId }),
          title: planTitle,
          start_date: planStartDate,
          end_date: adjustedEnd,
          is_main: false,
        },
      },
      {
        onSuccess: (response) => {
          // Get the newly created plan from the response
          const newPlan = response.data.length > 0 ? response.data[0] : response.data;
          // Reset form state
          setPlanTitle("");
          setPlanStartDate("");
          setPlanEndDate("");
          setValidationError("");
          setIsPopoverOpen(false);

          // Navigate to the edit route with the new plan
          if (isBrandRoute) {
            navigate(`/portal-brand/${brandId}/edit`, {
              state: {
                isBrand: true,
                brandId: brandId,
                brandName: brandName,
                brandLocationCount: brandLocationCount,
                planId: newPlan.id,
                // Force the effective location to be the plan's location
                forceLocationId: newPlan.location,
              },
            });
          } else {
            navigate(`/portal-location/${paramId}/edit`, {
              state: {
                isBrand: false,
                locationName: locationName,
                locationAddress: locationAddress,
                planId: newPlan.id,
              },
            });
          }
        },
        onError: (error) => {
          console.error("Error creating plan:", error);
          setValidationError("Failed to create portal. Please try again.");
        },
      }
    );
  };

  const [isHoverPopoverOpen, setIsHoverPopoverOpen] = useState(false);
  const [hoverPlanTitle, setHoverPlanTitle] = useState("");
  const [hoverPlanStartDate, setHoverPlanStartDate] = useState("");
  const [hoverPlanEndDate, setHoverPlanEndDate] = useState("");
  
  const handleCreateHoverPlan = () => {
    const adjustedEnd = adjustEndDateForBackend(hoverPlanEndDate);

    // Check for overlapping plans
    const allPlans = plansData?.data || [];
    const overlappingPlans = findOverlappingPlans(allPlans, hoverPlanStartDate, adjustedEnd);

    if (overlappingPlans.length > 0) {
      const overlappingPlanNames = overlappingPlans.map((plan) => plan.title).join(", ");
      setHoverValidationError(`This date range overlaps with an existing planned portal: ${overlappingPlanNames}`);
      return;
    }

    createPlan(
      {
        formData: {
          organisation: userProfile.organisation_id,
          ...(isBrandRoute ? { brand: paramId } : { location: effectiveLocationId }),
          title: hoverPlanTitle,
          start_date: hoverPlanStartDate,
          end_date: adjustedEnd,
          is_main: false,
        },
      },
      {
        onSuccess: (response) => {
          // Get the newly created plan from the response
          const newPlan = response.data.length > 0 ? response.data[0] : response.data;

          // Reset form state
          setHoverPlanTitle("");
          setHoverPlanStartDate("");
          setHoverPlanEndDate("");
          setHoverValidationError("");
          setIsHoverPopoverOpen(false);

          // Navigate to the edit route with the new plan
          if (isBrandRoute) {
            navigate(`/portal-brand/${brandId}/edit`, {
              state: {
                isBrand: true,
                brandId: brandId,
                brandName: brandName,
                brandLocationCount: brandLocationCount,
                planId: newPlan.id,
                // Force the effective location to be the plan's location
                forceLocationId: newPlan.location,
              },
            });
          } else {
            navigate(`/portal-location/${paramId}/edit`, {
              state: {
                isBrand: false,
                locationName: locationName,
                locationAddress: locationAddress,
                planId: newPlan.id,
              },
            });
          }
        },
        onError: (error) => {
          console.error("Error creating hover plan:", error);
          setHoverValidationError("Failed to create portal. Please try again.");
        },
      }
    );
  };

  const handleAddEventForDate = (date) => {
    // Format the date for the form
    const formatDateForBackend = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    const formattedDate = formatDateForBackend(date);

    // Set the hover popover state (not the header popover state)
    setHoverPlanStartDate(formattedDate);
    setHoverPlanEndDate(formattedDate);
    setHoverPlanTitle("");
    setHoverValidationError(""); // Clear any previous validation errors
    setIsHoverPopoverOpen(true); // Only open hover popover
  };
  
  const handleCreateOrUpdateHoverPlan = () => {
    handleCreateHoverPlan();
  };
  
  const handleHoverPopoverChange = (open) => {
    setIsHoverPopoverOpen(open);
    if (!open) {
      setHoverValidationError(""); // Clear validation error when popover closes
    }
  };

  // Handle plan click, including resetting selected period
  const handlePlanClick = useCallback((plan) => {
    setSelectedPlan(plan);
    // Reset selected period when plan changes
    setSelectedPeriod(null);
  }, []);

  // Handle period selection
  const handlePeriodClick = useCallback((period) => {
    setSelectedPeriod(period);

    // Reset any specific period settings when changing periods
    if (!period?.guest_portal_setting) {
      // If no guest portal setting is found for this period,
      // we'll fall back to plan settings in the extractSetting helper
    }
  }, []);

  const handleMonthClick = useCallback((month) => {
    // Logic for handling month click, if needed
  }, []);

  // Set default selected period when periods data changes or plan changes
  useEffect(() => {
    // Reset selected period when plan changes
    if (periodsData?.data?.length > 0) {
      // Sort periods chronologically by start_time
      const sortedPeriods = [...periodsData.data].sort((a, b) => {
        const aHour = parseInt(a.start_time.split(":")[0], 10);
        const bHour = parseInt(b.start_time.split(":")[0], 10);
        return aHour - bHour;
      });

      // Select the first period by default
      setSelectedPeriod(sortedPeriods[0]);
    } else {
      setSelectedPeriod(null);
    }
  }, [periodsData, selectedPlan]);

  // Create a unified mockup component that uses our helper function
  const renderMockup = (deviceType) => {
    return (
      <CaptivePortalMockup
        device={deviceType}
        scaleFactor={scaleFactorMobile}
        logoPreview={extractSetting("logo")}
        backgroundPreview={displayBackgroundPreview}
        backgroundOverlayColor={colorConversion(displayBackgroundOverlayColor, displayBackgroundOverlayOpacity)}
        buttonColor={displayButtonColor}
        buttonTextColor={displayButtonTextColor}
        email={extractSetting("email_flag")}
        age={extractSetting("age_flag")}
        dob={extractSetting("dob_flag")}
        gender={extractSetting("gender_flag")}
        facebook={extractSetting("facebook_flag")}
        firstName={extractSetting("first_name_flag")}
        lastName={extractSetting("last_name_flag")}
        phoneNumber={extractSetting("phone_number_flag")}
        message={extractSetting("message")}
        activeTab={activeTab}
        links_data={
          extractSetting("links_flag") && displayLinks?.length > 0
            ? {
                links: displayLinks,
              }
            : null
        }
        banner_data={
          extractSetting("banner_flag") && displayBannerData?.data
            ? {
                title: displayBannerData?.data?.title,
                description: displayBannerData?.data?.description,
                banner_url: displayBannerData?.data?.banner_url,
                image: displayBannerData?.data?.image,
                image_filename: displayBannerData?.data?.image_filename,
              }
            : null
        }
        segmentView={"page"}
        redirectUrl={extractSetting("redirect_url")}
        orgDetails={orgDetails?.data}
      />
    );
  };

  return (
    <Flex direction="column" gap="5" p="5" style={{ alignSelf: "stretch", width: "100%", height: "100%" }}>
      {/* Navigation header with back button */}
      <Flex style={{ justifyContent: "space-between", alignItems: "center" }}>
        <Flex gap="4" style={{ alignItems: "center", flex: "1 0 0" }}>
          <IconButton size="2" variant="soft" color="gray" onClick={() => navigate("/portal-selector")}>
            <ArrowLeftIcon width={16} height={16} />
          </IconButton>
          <Text size="2" weight="regular" color="gray">
            Captive Portal
          </Text>
        </Flex>
        <Flex gap="1" direction="column" style={{ justifyContent: "center", alignItems: "center" }}>
          <Text size="2" weight="medium">
            {isBrand ? brandName : locationName}
          </Text>
          {isBrand && brandLocationCount > 0 ? (
            <HoverCard.Root>
              <HoverCard.Trigger>
                <Text size="1" color="gray">
                  {brandLocationCount === 1 ? "1 location" : `${brandLocationCount} locations`}
                </Text>
              </HoverCard.Trigger>
              <HoverCard.Content maxHeight="300px" style={{ padding: "var(--space-3)" }}>
                <Flex direction="column" gap="3">
                  {locationsData
                    ?.filter((loc) => loc.brand_id === brandId)
                    .map((loc) => {
                      const live = liveAccessPointsStatus.find((l) => l.name === loc.name) || {};
                      const online = live.online_access_points_count ?? loc.online_access_points_count ?? 0;
                      const offline = live.offline_access_points_count ?? loc.offline_access_points_count ?? 0;
                      const statusColor = online > 0 && offline === 0 ? "var(--grass-9)" : online > 0 && offline > 0 ? "var(--amber-9)" : "var(--red-9)";
                      const isOnline = online > 0 && offline === 0;
                      const isPartial = online > 0 && offline > 0;
                      return (
                        <Flex key={loc.id} direction="column" gap="1">
                          <Flex align="center" gap="1">
                            <Tooltip content={isOnline ? "Online" : isPartial ? "Partially online" : "Offline"}>
                              <Box style={{ background: statusColor, width: "8px", height: "8px", borderRadius: "4px" }} />
                            </Tooltip>
                            <Text size="1" weight="medium">
                              {loc.name}
                            </Text>
                          </Flex>
                          <Text size="1" color="gray">
                            {loc.address || "No address"}
                          </Text>
                        </Flex>
                      );
                    })}
                </Flex>
              </HoverCard.Content>
            </HoverCard.Root>
          ) : (
            <Text size="1" color="gray">
              {isBrand ? "0 locations" : locationAddress}
            </Text>
          )}
        </Flex>
        <Flex gap="2" style={{ flex: "1 0 0", justifyContent: "flex-end", alignItems: "center" }}>
          <Button
            variant="soft"
            color="gray"
            onClick={() => {
              // Find the main plan
              const mainPlan = plansData?.data?.find((plan) => plan.is_main);

              console.log("Found main plan:", mainPlan);
              console.log("All plans data:", plansData?.data);

              if (isBrandRoute) {
                navigate(`/portal-brand/${brandId}/edit`, {
                  state: {
                    isBrand: true,
                    brandId: brandId,
                    brandName: brandName,
                    brandLocationCount: brandLocationCount,
                    planId: mainPlan?.id || null,
                    // Force the effective location to be the main plan's location
                    forceLocationId: mainPlan?.location,
                  },
                });
              } else {
                navigate(`/portal-location/${paramId}/edit`, {
                  state: {
                    isBrand: false,
                    locationName: locationName,
                    locationAddress: locationAddress,
                    planId: mainPlan?.id || null,
                  },
                });
              }
            }}
          >
            Edit main
          </Button>
        </Flex>
      </Flex>
      {/* Main content area */}
      <Flex gap="5" style={{ alignItems: "center", flex: "1 0 0", alignSelf: "stretch", width: "100%", height: "100%" }}>
        {/* Calendar planner */}
        <Flex
          direction="column"
          style={{
            flex: "1 0 0",
            alignItems: "flex-start",
            alignSelf: "stretch",
            width: "100%",
            height: "100%",
            border: "1px solid var(--gray-a6)",
            overflow: "hidden",
            borderRadius: "var(--radius-4)",
          }}
        >
          {/* Calendar Component with Plans */}
          <Calendar
            onDateSelect={handleDateSelect}
            plans={displayPlans}
            onAddEventForDate={handleAddEventForDate}
            CreatePlanPopover={CreatePlanPopover}
            planTitle={hoverPlanTitle}
            setPlanTitle={setHoverPlanTitle}
            planStartDate={hoverPlanStartDate}
            setPlanStartDate={setHoverPlanStartDate}
            planEndDate={hoverPlanEndDate}
            setPlanEndDate={setHoverPlanEndDate}
            onCreatePlan={handleCreateOrUpdateHoverPlan}
            isCreating={creatingPlan}
            hoverPopoverOpen={isHoverPopoverOpen}
            setHoverPopoverOpen={handleHoverPopoverChange}
            selectedPlan={selectedPlan}
            onPlanSelect={handleSelectPlan}
            validationError={hoverValidationError}
            setValidationError={setHoverValidationError}
            addEventButton={
              <CreatePlanPopover
                isOpen={isPopoverOpen}
                onOpenChange={setIsPopoverOpen}
                planTitle={planTitle}
                setPlanTitle={setPlanTitle}
                planStartDate={planStartDate}
                setPlanStartDate={setPlanStartDate}
                planEndDate={planEndDate}
                setPlanEndDate={setPlanEndDate}
                onCreatePlan={handleCreatePlan}
                isCreating={creatingPlan}
                validationError={validationError}
                setValidationError={setValidationError}
              >
                <IconButton variant="soft" size="2">
                  <PlusIcon width={16} height={16} />
                </IconButton>
              </CreatePlanPopover>
            }
          />
        </Flex>
        {/* Captive portal mockup viewer */}
        <Flex
          direction="column"
          style={{
            flex: "1 0 0",
            alignItems: "flex-start",
            alignSelf: "stretch",
            width: "100%",
            height: "100%",
            border: "1px solid var(--gray-a6)",
            overflow: "hidden",
            borderRadius: "var(--radius-4)",
          }}
        >
          {/* Header section */}
          <Flex gap="2" p="4" style={{ justifyContent: "space-between", alignItems: "center", alignSelf: "stretch", background: "var(--color-panel-solid)" }}>
            {/* Badge - Update to show selected plan or active plan */}
            <Flex gap="2" style={{ alignItems: "center", flex: "1 0 0" }}>
              {/* Show green dot if the active plan is main and is selected or no plan is selected */}
              {((!selectedPlan && guestPortalSettings?.data.active_plan.is_main) || (selectedPlan && selectedPlan.id === guestPortalSettings?.data.active_plan.id && selectedPlan.is_main)) && (
                <Box style={{ background: "var(--grass-9)", width: "8px", height: "8px", borderRadius: "4px" }} />
              )}
              <Tooltip
                content={
                  selectedPlan
                    ? selectedPlan.is_main
                      ? (() => {
                          const now = new Date();
                          const nextCustomPlan = plansData?.data
                            ?.filter((plan) => !plan.is_main && new Date(plan.start_date) > now)
                            ?.sort((a, b) => new Date(a.start_date) - new Date(b.start_date))[0];
                          if (selectedPlan.id === guestPortalSettings?.data.active_plan.id) {
                            return (
                              <span style={{ whiteSpace: "pre-line" }}>
                                Default captive portal
                                {"\n"}
                                (Currently active until {nextCustomPlan ? formatDate(nextCustomPlan.start_date) : "end of schedule"})
                              </span>
                            );
                          } else {
                            return <span style={{ whiteSpace: "pre-line" }}>Default captive portal</span>;
                          }
                        })()
                      : selectedPlan.title === guestPortalSettings?.data.active_plan.title
                      ? `Currently active until ${formatDateMinusOne(guestPortalSettings?.data.active_plan.end_date)}`
                      : `Planned for ${formatDate(selectedPlan?.start_date)} - ${formatDateMinusOne(selectedPlan?.end_date)}`
                    : guestPortalSettings?.data.active_plan.is_main
                    ? (() => {
                        const now = new Date();
                        const nextCustomPlan = plansData?.data?.filter((plan) => !plan.is_main && new Date(plan.start_date) > now)?.sort((a, b) => new Date(a.start_date) - new Date(b.start_date))[0];
                        return (
                          <span style={{ whiteSpace: "pre-line" }}>
                            Default captive portal
                            {"\n"}
                            (Currently active until {nextCustomPlan ? formatDate(nextCustomPlan.start_date) : "end of schedule"})
                          </span>
                        );
                      })()
                    : `Until ${formatDateMinusOne(guestPortalSettings?.data.active_plan.end_date)}`
                }
                style={{ textAlign: "center" }}
              >
                <Badge
                  color={
                    selectedPlan
                      ? selectedPlan.is_main
                        ? "amber"
                        : selectedPlan.title === guestPortalSettings?.data.active_plan.title
                        ? "grass"
                        : "gray"
                      : guestPortalSettings?.data.active_plan.is_main
                      ? "amber"
                      : "grass"
                  }
                >
                  {selectedPlan ? (selectedPlan.is_main ? "Main" : selectedPlan.title) : guestPortalSettings?.data.active_plan.is_main ? "Main" : guestPortalSettings?.data.active_plan.title}
                </Badge>
              </Tooltip>
            </Flex>
            {/* Segment View */}
            <Flex gap="2" style={{ justifyContent: "center", alignItems: "center", flex: "1 0 0" }}>
              <SegmentedControl.Root defaultValue="portal" onValueChange={(value) => setActiveTab(value)}>
                <SegmentedControl.Item value="portal">Form</SegmentedControl.Item>
                <SegmentedControl.Item value="landing">Landing</SegmentedControl.Item>
              </SegmentedControl.Root>
            </Flex>
            {/* Mockup toggle and edit button */}
            <Flex gap="2" style={{ flex: "1 0 0", justifyContent: "flex-end", alignItems: "center" }}>
              <Tooltip content={`Switch to ${view === "mobile" ? "desktop" : "mobile"} view`}>
                <IconButton size="2" variant="soft" color="gray" onClick={() => setView(view === "mobile" ? "desktop" : "mobile")}>
                  {view === "mobile" ? <ComputerDesktopIcon width={16} height={16} /> : <DevicePhoneMobileIcon width={16} height={16} />}
                </IconButton>
              </Tooltip>
              <Button
                variant="soft"
                color="gray"
                onClick={() => {
                  if (isBrand) {
                    // For brand routes, we need to find which location owns the selected plan
                    const planLocationId = selectedPlan?.location;

                    navigate(`/portal-brand/${brandId}/edit`, {
                      state: {
                        isBrand: true,
                        brandId: brandId,
                        brandName: brandName,
                        brandLocationCount: brandLocationCount,
                        planId: selectedPlan?.id,
                        // Force the effective location to be the plan's location
                        forceLocationId: planLocationId,
                      },
                    });
                  } else {
                    navigate(`/portal-location/${paramId}/edit`, {
                      state: {
                        isBrand: false,
                        locationName: locationName,
                        locationAddress: locationAddress,
                        planId: selectedPlan?.id,
                      },
                    });
                  }
                }}
              >
                Edit
              </Button>
            </Flex>
          </Flex>
          <Separator size="4" orientation="horizontal" style={{ minHeight: "1px" }} />
          {/* Mockup content */}
          <Flex style={{ flex: "1 0 0", alignItems: "center", justifyContent: "center", alignSelf: "stretch" }}>
            {view === "mobile" ? (
              <IPhoneMockup screenWidth={screenWidthMobile} frameColor="var(--gray-12)" statusbarColor="var(--gray-contrast)" transparentNavBar="true" hideStatusBar="true" screenType="notch">
                <Suspense>{renderMockup(view)}</Suspense>
              </IPhoneMockup>
            ) : (
              // Desktop View
              <Suspense>
                <Flex style={{ flex: "1 0 0", alignSelf: "stretch", justifyContent: "center" }}>{renderMockup(view)}</Flex>
              </Suspense>
            )}
          </Flex>
          {/* Footer section with separator and period selector - only show when needed */}
          {selectedPlan && (isLoadingPeriods || periodsData?.data?.length > 1) && (
            <>
              <Separator size="4" orientation="horizontal" style={{ minHeight: "1px" }} />
              <Flex p="4" style={{ justifyContent: "space-between", alignItems: "center", alignSelf: "stretch", background: "var(--color-panel-solid)" }}>
                {isLoadingPeriods ? (
                  <Flex p="2" gap="2" style={{ flex: "1 0 0", alignItems: "center", justifyContent: "center", borderRadius: "var(--radius-2)" }}>
                    <Spinner size="3" />
                  </Flex>
                ) : (
                  <Flex style={{ flex: "1 0 0", width: "100%", borderRadius: "var(--radius-2)", overflow: "hidden" }}>
                    {/* Sort periods starting from 4 AM to 3:59 AM next day */}
                    {[...periodsData.data]
                      .sort((a, b) => {
                        const aHour = parseInt(a.start_time.split(":")[0], 10);
                        const bHour = parseInt(b.start_time.split(":")[0], 10);

                        // Adjust hours: 4AM-11:59PM (4-23) remain as is; 12AM-3:59AM (0-3) become 24-27
                        const adjustedAHour = aHour < 4 ? aHour + 24 : aHour;
                        const adjustedBHour = bHour < 4 ? bHour + 24 : bHour;

                        return adjustedAHour - adjustedBHour;
                      })
                      .map((period, index, array) => {
                        // Format time in 12-hour format with AM/PM (abbreviated)
                        const formatTime = (timeString) => {
                          const [hours, minutes] = timeString.split(":");
                          const hour = parseInt(hours, 10);
                          const ampm = hour >= 12 ? "PM" : "AM";
                          const displayHour = hour % 12 || 12;

                          // For simplified display, we'll round minutes to the nearest hour if minutes are close to 0 or 59
                          // This way "11:59PM" becomes "12AM" and "12:01AM" becomes "12AM"
                          if (minutes === "00" || minutes === "01" || minutes === "02") {
                            return `${displayHour}${ampm}`;
                          } else if (minutes === "58" || minutes === "59") {
                            // For minutes close to the next hour
                            const nextHour = (hour + 1) % 24;
                            const nextAmpm = nextHour >= 12 ? "PM" : "AM";
                            const nextDisplayHour = nextHour % 12 || 12;
                            return `${nextDisplayHour}${nextAmpm}`;
                          } else {
                            // For times not near an hour boundary, show the exact hour
                            return `${displayHour}${ampm}`;
                          }
                        };

                        const startTime = formatTime(period.start_time);
                        const endTime = formatTime(period.end_time);
                        const isSelected = selectedPeriod?.id === period.id;
                        const isFirst = index === 0;
                        const isLast = index === array.length - 1;
                        const periodWidth = `${100 / array.length}%`;

                        return (
                          <React.Fragment key={period.id}>
                            {index > 0 && <Separator orientation="vertical" size="2" style={{ height: "40px" }}></Separator>}
                            <Flex
                              onClick={() => handlePeriodClick(period)}
                              style={{
                                flex: 1,
                                width: periodWidth,
                                justifyContent: "center",
                                alignItems: "center",
                                padding: "8px",
                                height: "40px",
                                cursor: "pointer",
                                background: isSelected ? "var(--blue-a3)" : "var(--color-panel-solid)",
                                border: isSelected ? "1px solid var(--blue-a6)" : "1px solid var(--gray-a6)",
                                borderLeft: index > 0 ? "none" : isSelected ? "1px solid var(--blue-a6)" : "1px solid var(--gray-a6)",
                                borderRadius: isFirst && isLast ? "var(--radius-2)" : isFirst ? "var(--radius-2) 0 0 var(--radius-2)" : isLast ? "0 var(--radius-2) var(--radius-2) 0" : "0",
                              }}
                            >
                              <Text size="2" weight="regular">
                                {startTime}-{endTime}
                              </Text>
                            </Flex>
                          </React.Fragment>
                        );
                      })}
                  </Flex>
                )}
              </Flex>
            </>
          )}
        </Flex>
      </Flex>
      {/* End of main content */}
    </Flex>
  );
};

export default CaptivePortalPlanner;
